import LoadingScreen from 'components/LoadingScreen'
import Cookies from 'js-cookie'
import React, { createContext, Dispatch, PropsWithChildren, useEffect, useState } from 'react'
import Api from 'utils/Api'
import { StorageManager } from 'utils/StorageManager'
import { useReducerAsync } from 'utils/useReducerAsync'
import { sessionActionHandlers, SessionActionType, sessionReducer, SessionType } from './reducers/SessionReducer'

const storageManager = new StorageManager()

export const getStoredToken = (): string | null => {
  try {
    return Cookies.get('accessToken') || localStorage.getItem('accessToken') || null
  } catch {
    return null
  }
}

export const setStoredToken = (token: string | null) => {
  if (token) {
    try {
      const parts = token.split('.')
      let expires = new Date(Date.now() + 86400 * 1000 * 7) // fallback 7 hari
      if (parts.length === 3) {
        try {
          const payload = JSON.parse(atob(parts[1]))
          if (payload && payload.exp) {
            expires = new Date(payload.exp * 1000)
          }
        } catch (e) {
          void e
        }
      }
      Cookies.set('accessToken', token, { expires, path: '/' })
      localStorage.setItem('accessToken', token)
      Api.defaults.headers.common['Authorization'] = 'Bearer ' + token
    } catch (e) {
      void e
    }
  } else {
    try {
      Cookies.remove('accessToken', { path: '/' })
      Cookies.remove('accessToken')
      localStorage.removeItem('accessToken')
      delete Api.defaults.headers.common['Authorization']
    } catch (e) {
      void e
    }
  }
}

// Inisialisasi token secara sinkron sebelum render agar request API pertama tidak 401
const initialToken = getStoredToken()
if (initialToken) {
  Api.defaults.headers.common['Authorization'] = 'Bearer ' + initialToken
}

const initialState: SessionType = {
  loading: true,
  accessToken: initialToken,
  user: null,
  role: null,
}

export type AuthorizedParams = {
  roles?: string[]
  resource?: string
  scope?: string
  scopes?: string[]
}

const SessionContext = createContext<[SessionType, Dispatch<SessionActionType>]>([initialState, () => null])

const SessionProvider: React.FunctionComponent<{
  children?: React.ReactNode
}> = ({ children }: PropsWithChildren<unknown>) => {
  const [initializing, setInitializing] = useState<boolean>(true)
  const [state, dispatch] = useReducerAsync(sessionReducer, initialState, sessionActionHandlers)

  useEffect(() => {
    if (state.accessToken) {
      setStoredToken(state.accessToken)
      setInitializing(false)
      dispatch({ type: 'FETCH_USER_ME' })
    } else {
      setStoredToken(null)

      if (!window.location.pathname.startsWith('/auth')) {
        window.location.replace(process.env.PUBLIC_URL + '/auth/signin')
      }

      setInitializing(false)
      dispatch({ type: 'LOADING', loading: false })
    }
  }, [state.accessToken])

  return (
    <SessionContext.Provider value={[state, dispatch]}>
      {initializing ? (
        <LoadingScreen />
      ) : (
        children
      )}
    </SessionContext.Provider>
  )
}

export { SessionProvider, SessionContext }
